import { createTheme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles";

const customShadows: Shadows = [
  "none",
  "0px 2px 6px rgba(0,0,0,0.05)",
  "0px 4px 12px rgba(0,0,0,0.06)",
  ...Array(22).fill("0px 4px 12px rgba(0,0,0,0.06)"),
] as Shadows;

export const getTheme = (mode: "light" | "dark") =>
  createTheme({
    palette: {
      mode,

      primary: {
        main: "#0B1E2D", // Navy
      },

      secondary: {
        main: "#D4AF37", // Gold
      },

      background: {
        default: mode === "light" ? "#F8FAFC" : "#0B1E2D",
        paper: mode === "light" ? "#FFFFFF" : "#111827",
      },

      text: {
        primary: mode === "light" ? "#1F2937" : "#E5E7EB",
        secondary: "#6B7280",
      },

      success: {
        main: "#16A34A",
      },

      error: {
        main: "#EF4444",
      },
    },

    shape: { borderRadius: 12 },

    typography: {
      fontFamily: "Inter, sans-serif",
      h1: { fontWeight: 700 },
      h2: { fontWeight: 600 },
      h3: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 600 },
    },

    shadows: customShadows,

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: "background 0.3s ease",
          },
        },
      },

      // BUTTONS — primary stays whatever the project already styled it as.
      // Gold gradient is opt-in via `<Button variant="contained" color="secondary">`.
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
            padding: "8px 16px",
            transition: "all 0.2s ease",
            "&:active": {
              transform: "scale(0.98)",
            },
          },
        },
        variants: [
          {
            props: { variant: "contained", color: "secondary" },
            style: {
              background: "linear-gradient(135deg, #D4AF37, #F5D97E)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(135deg, #c49b2f, #e6c96a)",
              },
            },
          },
        ],
      },

      // NOTE: deliberately NO global MuiIconButton override.
      // Premium pill styling lives in Components/ui/ActionButtons.tsx only.

      // PAPER — picks up *any* Paper-backed surface (cards, dialogs, popovers,
      // accordion containers) so list pages that wrap their table in a Paper
      // automatically get the premium look without per-page sx.
      MuiPaper: {
        styleOverrides: {
          // `elevation: 0` papers (used for inset surfaces like the sidebar)
          // shouldn't pick up a global shadow — guard with `&.MuiPaper-elevation0`.
          root: {
            borderRadius: "12px",
            "&.MuiPaper-rounded": { borderRadius: "12px" },
          },
          elevation1: {
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          },
          elevation2: {
            boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          },
        },
      },

      // TABLE
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          },
        },
      },

      // Subtle premium head — uppercase + bold + soft navy gradient. Pages that
      // override head styling via `sx` still win; this only paints unstyled MUI tables.
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: mode === "light" ? "#0B1E2D" : "#E5E7EB",
              background:
                mode === "light"
                  ? "linear-gradient(180deg, rgba(11,30,45,0.04) 0%, #f8fafc 100%)"
                  : "rgba(255,255,255,0.03)",
            },
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: mode === "light" ? "#F9FAFB" : "rgba(255,255,255,0.04)",
            },
          },
        },
      },

      // CARD
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          },
        },
      },

      // INPUTS
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              transition: "box-shadow 0.2s ease",
              "&:focus-within": {
                boxShadow: "0 0 0 2px rgba(212,175,55,0.3)",
              },
            },
          },
        },
      },
    },
  });

// Backward-compat default export so existing `import theme from './theme'` keeps working.
const theme = getTheme("light");
export default theme;
